import { ReactSVG } from 'react-svg'
import Done from '../../assets/Icons/Done.svg'
import BackgroundActivity from '../../assets/Icons/Signin.svg'
import Windows from '../../assets/Icons/Platforms/Windows.svg'
import IntelliKeyIcon from '../../assets/Icons/IntelliKeyAppIcon.svg'
import IntelliKeyMark from '../../assets/Icons/IntelliKeySml.svg'
import {
  WINDOWS_SETUP_TASK_KEYS,
  completedWindowsSetupTaskCount
} from '../../classes/WindowsSetupStatus.js'
import './index.css'

const windowsSteps = [
  {
    key: 'inputMethodInstalled',
    title: 'Keyboard components installed',
    detail: 'The 64-bit and 32-bit IntelliKey input components are registered.',
    image: IntelliKeyIcon,
    action: 'refresh',
    actionLabel: 'Check again'
  },
  {
    key: 'inputSourceEnabled',
    title: 'IntelliKey English added',
    detail: 'IntelliKey is available as an English keyboard in Windows.',
    icon: Windows,
    action: 'openLanguageSettings',
    actionLabel: 'Open language settings'
  },
  {
    key: 'inputSourceSelected',
    title: 'Switch your active keyboard to IntelliKey',
    detail: 'Hold the Windows key, tap Space, then choose IntelliKey English from the input-source picker.',
    icon: IntelliKeyMark,
    action: 'refresh',
    actionLabel: 'Check selection',
    shortcut: true
  },
  {
    key: 'systemTextSuggestionsDisabled',
    title: 'Use only IntelliKey suggestions',
    detail: 'Turn off the Windows physical-keyboard prediction panel so it cannot cover or impersonate IntelliKey autocomplete.',
    icon: Windows,
    action: 'disableSystemTextSuggestions',
    actionLabel: 'Disable Windows suggestions'
  },
  {
    key: 'backgroundActivityEnabled',
    title: 'Ready after Windows sign-in',
    detail: 'IntelliKey Broker is enabled at startup so suggestions and managed IMEs are available.',
    icon: BackgroundActivity,
    action: 'openStartupSettings',
    actionLabel: 'Open startup settings'
  }
]

const WindowsSetupStep = ({ step, number, complete, action }) => {
  const className = [
    'windowsSetupStep',
    complete ? 'windowsSetupStepComplete' : 'windowsSetupStepPending',
    step.shortcut && !complete ? 'windowsSetupStepPrimary' : ''
  ].filter(Boolean).join(' ')

  return <div className={className}>
           <div className='windowsSetupStepState' aria-hidden='true'>
             {complete ? <ReactSVG src={Done}/> : number}
           </div>
           <div className='windowsSetupStepIcon' aria-hidden='true'>
             {step.icon && <ReactSVG src={step.icon}/>}
             {step.image && <img src={step.image} alt=''/>}
           </div>
           <div className='windowsSetupStepCopy'>
             <div className='windowsSetupStepTitle'>{step.title}</div>
             <div className='windowsSetupStepDetail'>{step.detail}</div>
             {step.shortcut && !complete &&
              <div className='windowsSetupShortcut' aria-label='Windows key plus Space'>
                <kbd><span className='windowsSetupWindowsGlyph'>⊞</span> Win</kbd>
                <span>+</span>
                <kbd>Space</kbd>
              </div>}
           </div>
           {!complete &&
            <button
              type='button'
              className='windowsSetupStepAction'
              onClick={() => action(step.action)}>
              {step.actionLabel}
            </button>}
         </div>
}

export const WindowsKeyboardSettings = ({ status, me }) => {
  const tasks = status && status.tasks ? status.tasks : {}
  const completed = completedWindowsSetupTaskCount(status)
  const percent = Math.round((completed / WINDOWS_SETUP_TASK_KEYS.length) * 100)
  const performAction = action => me.performWindowsSetupAction(action)

  return <main className='windowsSetupPage'>
           <header className='windowsSetupHeader'>
             <div className='windowsSetupBrand'>
               <img className='windowsSetupAppIcon' src={IntelliKeyIcon} alt='IntelliKey'/>
               <div>
                 <div className='windowsSetupEyebrow'>INTELLIKEY FOR WINDOWS</div>
                 <h1>Finish keyboard setup</h1>
                 <p>Windows has installed IntelliKey. Complete the remaining system step to start using it.</p>
               </div>
             </div>
             <div className='windowsSetupProgress' role='status' aria-live='polite'>
               <div className='windowsSetupProgressCopy'>
                 <span>Setup progress</span>
                 <strong>{completed} of {WINDOWS_SETUP_TASK_KEYS.length}</strong>
               </div>
               <div className='windowsSetupProgressTrack' aria-hidden='true'>
                 <div style={{ width: `${percent}%` }}/>
               </div>
             </div>
           </header>
           <section className='windowsSetupContent' aria-label='Windows setup steps'>
             {windowsSteps.map((step, index) =>
               <WindowsSetupStep
                 key={step.key}
                 step={step}
                 number={index + 1}
                 complete={tasks[step.key] === true}
                 action={performAction}/>
             )}
             <footer className='windowsSetupFooter'>
               <span>After switching keyboards, reopen any editor that was already running.</span>
               <button type='button' onClick={() => performAction('refresh')}>Refresh status</button>
             </footer>
           </section>
         </main>
}
